/**
 * FileGridCard — Individual Card in Grid View
 *s
 * Two distinct layouts based on entity type:
 *
 * Folder card (compact horizontal):
 *   [folder icon] [name + date]  [3-dots]
 *
 * File card (tall with thumbnail, matching Figma):
 *   ┌─────────────────────┐
 *   │   thumbnail / icon   │  ← 120px, image preview or file type SVG
 *   ├─────────────────────┤
 *   │ fileName             │  ← text-xs font-medium
 *   │ type | size | date   │  ← text-[11px] gray, separated by |
 *   └─────────────────────┘
 */

import OpenFolderIcon from "../../../../../../../../assets/svg/open-folder-icon";
import RowActionsMenu from "./RowActionsMenu";
import type { FileGridCardProps } from "../types/fileManagement.types";
import {
  FOLDER_ACTION_MENU_ITEMS,
  FILE_ACTION_MENU_ITEMS,
} from "../constants/fileManagement.constants";
import {
  isFolder,
  isImageType,
  formatDate,
  formatFileSize,
  getIconForFileType,
} from "../utils/fileManagement.utils";

const FileGridCard = ({
  item,
  onFolderClick,
  onFileClick,
  onRowAction,
}: FileGridCardProps) => {
  const isFolderEntity = isFolder(item);
  const isFileEntity = !isFolderEntity;

  const handleClick = () => {
    if (isFolderEntity) {
      onFolderClick(item);
    } else {
      onFileClick(item);
    }
  };

  // ── Folder Card (compact horizontal) ──
  if (isFolderEntity) {
    return (
      <div
        onClick={handleClick}
        className="group flex items-center gap-2.5 p-1.5 rounded-lg border border-border-gray dark:border-border-gray-dk cursor-pointer transition-colors hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
      >
        <div className="shrink-0 flex items-center justify-center p-1.5 rounded-lg bg-hover-effect dark:bg-hover-effect-dk">
          <OpenFolderIcon className="w-[28px] h-[28px] text-analytics-blue dark:text-analytics-blue-dk" />
        </div>
        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          <span className="text-md font-medium text-heading-text dark:text-heading-text-dk truncate leading-normal">
            {item.name}
          </span>
          <span className="text-sm font-normal text-analytics-gray dark:text-analytics-gray-dk leading-normal whitespace-nowrap">
            {formatDate(item.updated_at)}
          </span>
        </div>
        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 shrink-0">
          <RowActionsMenu
            entity={item}
            menuItems={FOLDER_ACTION_MENU_ITEMS}
            onRowAction={onRowAction}
          />
        </div>
      </div>
    );
  }

  // ── File Card (tall with thumbnail) ──
  if (isFileEntity) {
    const thumbnailUrl = item?.thumbnails?.low;
    const hasImagePreview =
      isImageType(item?.type) &&
      !!thumbnailUrl &&
      item?.thumbnail_status === "ready";
    const iconSrc = getIconForFileType(item?.type);
    const fileMenuItems = item?.short_url
      ? FILE_ACTION_MENU_ITEMS
      : FILE_ACTION_MENU_ITEMS?.filter((m) => m?.action !== "copy_link");

    return (
      <div
        onClick={handleClick}
        className="group flex flex-col gap-1.5 p-3 rounded-lg border border-border-gray dark:border-border-gray-dk cursor-pointer transition-colors"
      >
        {/* Thumbnail / Icon Area — 120px tall, with hover overlay + 3-dots */}
        <div className="relative flex items-center justify-center h-[120px] w-full rounded-lg bg-hover-effect-2 dark:bg-hover-effect-2-dk overflow-hidden">
          {hasImagePreview ? (
            <>
              <div
                className="absolute inset-0 bg-cover bg-center scale-110 blur-md opacity-70"
                style={{ backgroundImage: `url(${thumbnailUrl})` }}
              />
              <img
                src={thumbnailUrl}
                alt={item?.name}
                className="relative w-full h-full object-contain"
              />
            </>
          ) : (
            <img src={iconSrc} alt={item.type} className="w-[34px] h-[34px]" />
          )}

          {/* Hover overlay (dark tint) */}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg" />

          {/* 3-dots menu — top-right, visible on hover */}
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <RowActionsMenu
              entity={item}
              menuItems={fileMenuItems}
              onRowAction={onRowAction}
            />
          </div>
        </div>

        {/* File Name */}
        <p className="text-md font-medium text-heading-text dark:text-heading-text-dk truncate leading-normal">
          {item?.name}
        </p>

        {/* Metadata Row: type | size | date */}
        <div className="flex items-center gap-2 text-[11px] font-normal text-analytics-gray dark:text-analytics-gray-dk leading-normal">
          <span className="lowercase">{item.type}</span>
          <span className="text-border-gray dark:text-border-gray-dk">|</span>
          <span>{formatFileSize(item.size_in_bytes)}</span>
          <span className="text-border-gray dark:text-border-gray-dk">|</span>
          <span>{formatDate(item.updated_at)}</span>
        </div>
      </div>
    );
  }

  return null;
};

export default FileGridCard;
